import Color from 'color';

import type {CommonProps} from './types';

import {logger} from '../../utils/logger';
import {deepClone} from '../../utils/object/deepClone';

type Asset = any;

/**
 * @param colorValue [red, greed, blue, alpha]
 */
type ColorValue = [number, number, number, number];

type Layer = any;

type Shape = any;

type ShapeElement = any;

const colorifyLayer = (layer: Layer, colorValue: ColorValue) => {
  const shapeTypes = [
    'fl', // fill
    'st', // stroke
  ];

  const shapes = (layer.shapes || []).map((shape: Shape) => {
    if (shape.it) {
      const it = shape.it.map((itItem: ShapeElement) => {
        if (shapeTypes.includes(itItem.ty)) {
          return {
            ...itItem,
            c: {
              ...itItem.c,
              k: colorValue,
            },
          };
        }

        return itItem;
      });

      return {
        ...shape,
        it,
      };
    }

    if (shape.c?.k) {
      if (shape.c.k[0]?.s) {
        return {
          ...shape,
          c: {
            ...shape.c,
            k: shape.c.k.map(item => {
              if (item?.s) {
                return {
                  ...item,
                  s: colorValue,
                };
              }

              return item;
            }),
          },
        };
      }

      // if it's plain color value
      if (
        shape.c.k.length === 4 &&
        shape.c.k.every(item => typeof item === 'number')
      ) {
        return {
          ...shape,
          c: {
            ...shape.c,
            k: colorValue,
          },
        };
      }
    }

    return shape;
  });

  return {
    ...layer,
    shapes,
  };
};

const getLayerName = (layer: Layer) => layer.nm.trim();

type Params = {
  color: string;
  layers?: string[];
  source: CommonProps['source'];
};

export const lottieColorify = ({
  color,
  layers = [],
  source,
}: Params): CommonProps['source'] => {
  if (typeof source !== 'object' || !source.layers) {
    return source;
  }

  try {
    const sourceCopy = deepClone(source);

    const rgbValues = Color(color).object();

    const rFraction = rgbValues.r! / 255;
    const gFraction = rgbValues.g! / 255;
    const bFraction = rgbValues.b! / 255;

    const colorValue: ColorValue = [rFraction, gFraction, bFraction, 1];

    const layersToColorify = layers.length
      ? layers
      : sourceCopy.layers.map(getLayerName);

    const result = {
      ...sourceCopy,
      assets: sourceCopy.assets.map((asset: Asset) => {
        const assetId = asset.id;
        const linkedLayer = sourceCopy.layers.find(
          (layer: Layer) => layer.refId === assetId,
        );

        if (linkedLayer) {
          const linkedLayerName = getLayerName(linkedLayer);

          if (layersToColorify.includes(linkedLayerName)) {
            return {
              ...asset,
              layers: asset.layers.map((layer: Layer) =>
                colorifyLayer(layer, colorValue),
              ),
            };
          }
        }
      }),
      layers: sourceCopy.layers.map((layer: Layer) => {
        const layerName = getLayerName(layer);

        if (layersToColorify.includes(layerName)) {
          return colorifyLayer(layer, colorValue);
        }

        return layer;
      }),
    };

    return result;
  } catch (error) {
    logger.error(error);

    return source;
  }
};
